import { useMessageNotification } from "context/MessageNotificationContext";
import { useSessionContext } from "context/SessionContext";

import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Pagination as MDPagination,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";

import MDBox from "components/MDBox";

import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";

import { LanguageContext } from "context";
import { useNotification } from "context/NotificationContext";

import {
  markAsReadNotification,
  markAllAsReadNotification,
  deleteNotification,
} from "../../../../shared/api/notification";

const ITEMS_PER_PAGE = 5;

export default function MessageCenter(): JSX.Element {
  const { messages, markAsRead, markAllAsRead } = useMessageNotification();
  const { accessToken } = useSessionContext();
  const navigate = useNavigate();
  const { t } = useContext(LanguageContext) as any;
  const { showNotification } = useNotification();
  const { removeMessage } = useMessageNotification();

  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(1);

  const filteredMessages = useMemo(() => {
    return tabIndex === 0 ? messages.filter((m) => !m.read) : messages.filter((m) => m.read);
  }, [messages, tabIndex]);

  const paginatedMessages = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredMessages.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMessages, page]);

  useEffect(() => {
    if (accessToken === null) {
      navigate("/pages/auth/SignIn");
    }
  }, [accessToken]);

  useEffect(() => {
    setPage(1); // タブ切り替え時に1ページ目に戻す
  }, [tabIndex]);

  if (accessToken === undefined) {
    return <Typography sx={{ p: 4 }}>{t("MessageCenter.loading")}</Typography>;
  }

  useEffect(() => {
    console.log("全メッセージ:", messages);
  }, [messages]);

  useEffect(() => {
    console.log(`フィルター結果(${tabIndex === 0 ? "未読" : "既読"})`, filteredMessages);
  }, [filteredMessages]);

  useEffect(() => {
    console.log("ページ", page, "のメッセージ:", paginatedMessages);
  }, [paginatedMessages, page]);

  const formatDateTime = (iso?: string) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "";
    return `[${date
      .toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replaceAll("/", "-")}]`;
  };

  const handleMarkAllAsRead = async () => {
    try {
      if (messages.length == 0) {
        return;
      }
      await markAllAsReadNotification(accessToken);
      markAllAsRead();
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("Common.updated-success"),
        },
        3000
      );
    } catch (error) {
      console.error("全通知既読エラー:", error);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        3000
      );
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await markAsReadNotification(accessToken, id);
      markAsRead(id);
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("Common.updated-success"),
        },
        3000
      );
    } catch (error) {
      console.error("通知既読エラー:", error);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        3000
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteNotification(accessToken, id);
      removeMessage(id);
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("Common.delete-success"),
        },
        3000
      );
    } catch (error) {
      console.error("通知削除エラー:", error);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.delete-error"),
        },
        3000
      );
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ margin: 4, padding: 2, minHeight: "60vh" }}>
        <CardContent>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", mb: 0 }}
            >
              {t("MessageCenter.title")}
              <IconButton
                onClick={handleMarkAllAsRead}
                title={t("MessageCenter.markAllAsRead")}
                size="small"
              >
                <MarkEmailReadIcon />
              </IconButton>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("MessageCenter.title-memo")}
            </Typography>
          </MDBox>

          <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
            <Tab label={t("MessageCenter.unreadTab")} />
            <Tab label={t("MessageCenter.readTab")} />
          </Tabs>

          {filteredMessages.length === 0 ? (
            <Typography sx={{ mt: 2 }}>{t("MessageCenter.empty")}</Typography>
          ) : (
            <>
              {paginatedMessages.map((msg) => (
                <Card
                  key={msg.id}
                  sx={{
                    mb: 2,
                    backgroundColor: msg.read ? "#f5f5f5" : "#fffde7",
                  }}
                  variant="outlined"
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {msg.title}
                    </Typography>
                    <Typography variant="body2">{msg.content}</Typography>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {formatDateTime(msg.datetime)}
                      </Typography>
                      <MDBox display="flex" alignItems="center">
                        {!msg.read && (
                          <IconButton
                            onClick={() => handleMarkAsRead(msg.id)}
                            title={t("MessageCenter.markAsRead")}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => handleDelete(msg.id)}
                          title={t("MessageCenter.delete")}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </MDBox>
                    </MDBox>
                  </CardContent>
                </Card>
              ))}
            </>
          )}

          {filteredMessages.length > ITEMS_PER_PAGE && (
            <MDPagination
              count={Math.ceil(filteredMessages.length / ITEMS_PER_PAGE)}
              page={page}
              onChange={(_, value) => setPage(value)}
              sx={{ mt: 2, display: "flex", justifyContent: "center" }}
            />
          )}
        </CardContent>
      </Card>
      <Footer />
    </DashboardLayout>
  );
}
